import { useEffect, useRef, useState } from 'react';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';
import { EMPTY_PRESENTATION, type Presentation } from './printTypes.js';

/**
 * The logo that appears on documents this application prints.
 *
 * One per company, set by CFOAI. It does not touch Xero: Xero has its own
 * branding themes and its own PDFs, and the two are deliberately separate
 * things — this is for the bilingual layout we print ourselves, where the
 * Arabic has to read properly.
 */

export interface BrandingFooter {
  email: string;
  phone: string;
  address: string;
}

export interface Branding {
  organisation: string;
  hasLogo: boolean;
  logoUrl: string | null;
  contentType: string | null;
  fileName: string | null;
  uploadedAt: string | null;
  bytes: number;
  footer: BrandingFooter;
  presentation: Presentation;
}

const MAX_BYTES = 256 * 1024;
const ALLOWED = ['image/png', 'image/jpeg', 'image/webp'];

export function BrandingScreen({
  branding,
  busy,
  error,
  version,
  t,
  onUpload,
  onRemove,
  onSave,
}: {
  branding: Branding | null;
  busy: boolean;
  error: string | null;
  /** Bumped after each change, to defeat the browser's cache of the image. */
  version: number;
  t: Translate;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onSave: (footer: BrandingFooter, presentation: Presentation) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const [footer, setFooter] = useState<BrandingFooter>({ email: '', phone: '', address: '' });
  const [saved, setSaved] = useState(false);

  // Seeded from the record, and re-seeded only when the stored values actually
  // differ. Depending on branding.footer itself would re-run on every reload,
  // because each response is a new object — which is what wiped the
  // confirmation the instant a save finished.
  const stored = branding?.footer;
  useEffect(() => {
    setFooter(stored ?? { email: '', phone: '', address: '' });
  }, [branding?.organisation, stored?.email, stored?.phone, stored?.address]);

  const [look, setLook] = useState<Presentation>(EMPTY_PRESENTATION);

  // Compared as text rather than by identity, for the same reason as the footer
  // above: every response is a new object.
  const storedLook = branding?.presentation;
  const storedLookKey = JSON.stringify(storedLook ?? null);
  useEffect(() => {
    setLook(storedLook ?? EMPTY_PRESENTATION);
  }, [branding?.organisation, storedLookKey]);

  const part = (key: 'taglineAr' | 'taglineEn' | 'accentColour' | 'bandColour' | 'terms') => ({
    value: look[key],
    onChange: (event: { target: { value: string } }) => {
      setLook((current) => ({ ...current, [key]: event.target.value }));
      setSaved(false);
    },
  });

  /*
    Whether anything on the page differs from what is stored.

    Compared as text: every response is a new object, so identity says nothing,
    and this page holds a nested record where a shallow check would miss a
    changed bank field.
  */
  const storedState = JSON.stringify({
    footer: stored ?? { email: '', phone: '', address: '' },
    look: storedLook ?? EMPTY_PRESENTATION,
  });
  const currentState = JSON.stringify({ footer, look });
  const dirty = storedState !== currentState;

  const save = () => {
    // Awaited: saying "Saved" before the server has answered would say it just
    // as confidently when the server refused.
    void onSave(footer, look).then(
      () => setSaved(true),
      () => setSaved(false),
    );
  };

  const bankPart = (key: keyof Presentation['bank']) => ({
    value: look.bank[key],
    onChange: (event: { target: { value: string } }) => {
      setLook((current) => ({ ...current, bank: { ...current.bank, [key]: event.target.value } }));
      setSaved(false);
    },
  });

  const field = (key: keyof BrandingFooter) => ({
    value: footer[key],
    onChange: (event: { target: { value: string } }) => {
      setFooter((current) => ({ ...current, [key]: event.target.value }));
      setSaved(false);
    },
  });

  const choose = (file: File | undefined) => {
    setLocalError(null);
    if (!file) return;
    // Checked here as well as on the server, so the answer arrives instantly
    // and names the file the person actually picked.
    if (!ALLOWED.includes(file.type)) {
      setLocalError('That has to be a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('That image is too large. The limit is 256 KB.');
      return;
    }
    onUpload(file);
  };

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '760px' }}>
      <PageTitle
        title={t('Branding')}
        sub={t('The logo on documents this app prints. Xero keeps its own branding for the PDFs it produces.')}
      />
      <div style={{ height: '16px' }} />

      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {branding?.organisation ?? ''}
        </div>

        <div style={{ padding: '18px 16px' }}>
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              height: '120px',
              border: `1px dashed ${theme.borderStrong}`,
              borderRadius: theme.radiusCard,
              background: theme.surfaceMuted,
              marginBottom: '14px',
            }}
          >
            {branding?.logoUrl ? (
              <img
                src={`${branding.logoUrl}&r=${version}`}
                alt={t('The current logo')}
                style={{ maxHeight: '96px', maxWidth: '300px', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ fontSize: '12.5px', color: theme.textMuted }}>
                {t('No logo yet. Documents will print with the company name only.')}
              </span>
            )}
          </div>

          <input
            ref={input}
            type="file"
            accept={ALLOWED.join(',')}
            hidden
            onChange={(event) => {
              choose(event.target.files?.[0]);
              // Cleared so choosing the same file twice still counts as a change.
              event.target.value = '';
            }}
          />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => input.current?.click()}
              style={{
                background: busy ? theme.borderStrong : theme.nav,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                padding: '10px 17px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {branding?.hasLogo ? t('Replace the logo') : t('Upload a logo')}
            </button>

            {branding?.hasLogo && (
              <button
                type="button"
                disabled={busy}
                onClick={onRemove}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  padding: '9px 15px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  color: theme.bad,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {t('Remove')}
              </button>
            )}

            <span style={{ fontSize: '11.5px', color: theme.textMuted }}>
              {t('PNG, JPEG or WebP. Up to 256 KB.')}
            </span>
          </div>

          {(localError || error) && (
            <div style={{ marginTop: '12px', fontSize: '12.5px', color: theme.badDeep, fontWeight: 600 }}>
              {t(localError ?? error ?? '')}
            </div>
          )}

          {branding?.hasLogo && branding.uploadedAt && (
            <div style={{ marginTop: '12px', fontSize: '11.5px', color: theme.textMuted }}>
              {t('Uploaded')} {branding.uploadedAt.slice(0, 10)}
              {branding.fileName ? ` · ${branding.fileName}` : ''} ·{' '}
              {Math.round(branding.bytes / 1024)} KB
            </div>
          )}
        </div>
      </Card>

      <div style={{ height: '18px' }} />

      {/*
        The details printed at the foot of the document. The client's own: this
        sheet is sent by the client to the client's customer, so CFOAI's address
        on it would send that customer's questions to the wrong firm. Left empty
        it simply prints nothing, which is correct.
      */}
      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('Footer on printed documents')}
        </div>

        <div style={{ padding: '18px 16px', display: 'grid', gap: '14px' }}>
          <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.6 }}>
            {t("The company's own contact details, as its customers should see them. Leave a field empty and it is left off the page.")}
          </div>

          <Line label={t('Address')} placeholder={t('Street, area, city, country')} t={t} {...field('address')} />
          <Line label={t('Email')} placeholder="accounts@example.qa" t={t} {...field('email')} />
          <Line label={t('Telephone')} placeholder="+974 0000 0000" t={t} {...field('phone')} />

        </div>
      </Card>

      <div style={{ height: '18px' }} />

      {/*
        A company that already had an invoice design keeps it. The layout is a
        choice here and everything inside it is a field, so the next company
        with a designer needs no new code.
      */}
      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('Layout of printed documents')}
        </div>

        <div style={{ padding: '18px 16px', display: 'grid', gap: '14px' }}>
          <label style={{ display: 'grid', gap: '5px' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>{t('Layout')}</span>
            <select
              value={look.template}
              onChange={(event) => {
                setLook((current) => ({ ...current, template: event.target.value }));
                setSaved(false);
              }}
              style={{
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '9px 11px',
                fontSize: '13px',
                fontFamily: 'inherit',
                background: theme.surface,
                color: 'inherit',
              }}
            >
              <option value="standard">{t('Plain bilingual sheet')}</option>
              <option value="banded">{t("The company's own design")}</option>
            </select>
          </label>

          {look.template === 'banded' && (
            <>
              <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.6 }}>
                {t('A coloured band across the head and foot, the slogan beside the logo, and bank details under the totals.')}
              </div>

{/*
                Two boxes rather than two lines of text: a slogan is usually set
                over two lines, and a single-line input drops the break without
                saying so. The sheet keeps whatever breaks are typed here.
              */}
              <Area label={t('Slogan (Arabic)')} rows={2} t={t} {...part('taglineAr')} />
              <Area label={t('Slogan (English)')} rows={2} t={t} {...part('taglineEn')} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Colour label={t('Accent colour')} fallback="#9b2242" t={t} {...part('accentColour')} />
                <Colour label={t('Band colour')} fallback="#fbe9cf" t={t} {...part('bandColour')} />
              </div>

              <div style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted, marginTop: '4px' }}>
                {t('Bank details, as they should appear on the invoice')}
              </div>
              <Line label={t('Bank')} placeholder="QNB" t={t} {...bankPart('name')} />
              <Line label={t('Account name')} placeholder="" t={t} {...bankPart('accountName')} />
              <Line label={t('Account number')} placeholder="" t={t} {...bankPart('accountNumber')} />
              <Line label={t('IBAN')} placeholder="" t={t} {...bankPart('iban')} />

              <Area label={t('Terms and conditions')} rows={4} t={t} {...part('terms')} />
            </>
          )}
        </div>
      </Card>

      {/*
        One save for the whole page, kept in view.

        It used to sit inside the first card, which meant that by the time
        somebody had scrolled down to choose a layout and fill in the bank
        details, the only way to keep any of it was to scroll back up to a
        button that looked like it belonged to something else.
      */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          marginTop: '18px',
          padding: '14px 16px',
          background: theme.surface,
          border: `1px solid ${dirty ? theme.nav : theme.dividerSoft}`,
          borderRadius: theme.radiusCard,
          boxShadow: theme.shadowCard,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={save}
          style={{
            background: busy || !dirty ? theme.borderStrong : theme.nav,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: busy || !dirty ? 'default' : 'pointer',
          }}
        >
          {busy ? t('Saving…') : t('Save changes')}
        </button>

        {dirty && !busy && (
          <button
            type="button"
            onClick={() => {
              setFooter(stored ?? { email: '', phone: '', address: '' });
              setLook(storedLook ?? EMPTY_PRESENTATION);
              setSaved(false);
            }}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.borderStrong}`,
              borderRadius: theme.radiusControl,
              padding: '9px 15px',
              fontSize: '12.5px',
              fontWeight: 600,
              color: theme.textMuted,
              cursor: 'pointer',
            }}
          >
            {t('Discard')}
          </button>
        )}

        <span style={{ fontSize: '12px', color: theme.textMuted }}>
          {busy ? '' : dirty ? t('Not saved yet.') : saved ? t('Saved.') : t('Everything here is saved.')}
        </span>

        {error && !busy && (
          <span style={{ fontSize: '12px', color: theme.badDeep, fontWeight: 600 }}>{t(error)}</span>
        )}
      </div>
    </div>
  );
}

/** Text that may run to more than one line, and keeps the breaks typed into it. */
function Area({
  label,
  rows,
  value,
  onChange,
  t,
}: {
  label: string;
  rows: number;
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  t: Translate;
}) {
  return (
    <label style={{ display: 'grid', gap: '5px' }}>
      <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>{t(label)}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={onChange}
        style={{
          border: `1px solid ${theme.borderStrong}`,
          borderRadius: theme.radiusControl,
          padding: '9px 11px',
          fontSize: '13px',
          fontFamily: 'inherit',
          lineHeight: 1.6,
          resize: 'vertical',
          background: theme.surface,
          color: 'inherit',
        }}
      />
    </label>
  );
}

/** A colour, typed or picked, with the layout's own default shown when empty. */
function Colour({
  label,
  fallback,
  value,
  onChange,
  t,
}: {
  label: string;
  fallback: string;
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  t: Translate;
}) {
  return (
    <label style={{ display: 'grid', gap: '5px' }}>
      <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>{t(label)}</span>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="color"
          value={/^#[0-9a-f]{6}$/i.test(value) ? value : fallback}
          onChange={onChange}
          style={{
            width: '38px',
            height: '36px',
            padding: 0,
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            background: theme.surface,
            cursor: 'pointer',
          }}
        />
        <input
          type="text"
          dir="ltr"
          value={value}
          placeholder={fallback}
          onChange={onChange}
          style={{
            flex: 1,
            minWidth: 0,
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '9px 11px',
            fontSize: '13px',
            fontFamily: theme.fontMono,
            background: theme.surface,
            color: 'inherit',
          }}
        />
      </div>
    </label>
  );
}

function Line({
  label,
  placeholder,
  value,
  onChange,
  t,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  t: Translate;
}) {
  return (
    <label style={{ display: 'grid', gap: '5px' }}>
      <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>{t(label)}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        style={{
          border: `1px solid ${theme.borderStrong}`,
          borderRadius: theme.radiusControl,
          padding: '9px 11px',
          fontSize: '13px',
          font: 'inherit',
          fontFamily: 'inherit',
          background: theme.surface,
          color: 'inherit',
        }}
      />
    </label>
  );
}
